import { Box } from '@mui/material';
import i18n from '../../i18n';
import CardView from '../../component/card/card-view';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import { useTranslation } from 'react-i18next';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import React, { useEffect, useState } from 'react';
import { ConditionType, SearchKeyInput, SearchKeyInputItems, ConditionsBox } from '../../component/form/search-key-input.component';
import { toJS } from 'mobx';
import { ResellerConsumptionState } from './reseller-consumption.state';
import { ResellerConsumption } from '../../models';
import moment from 'moment';
import PeriodDateFilter from '../../component/period-date-filter.component';
import './scss/reseller-consumption.style.scss';
import ButtonComponent from '../../component/button/button.component';
import SensitiveOperationsDetail from '../sensitive-operation-record/sensitive-operations-detail';
import { OperationType } from '../../models/sensitive-record.entity';
import { getResellerFilter } from '../../constants/tool.reseller';
import { formatEUR } from '../../constants/tool.fn';
import { getAuthPointI18n } from '../../constants/tool-auth-point';

// 经销商管理列表组件
const ResellerConsumptionPage = observer(() => {
    useTranslation();

    // 使用数据管理
    const resellerConsumptionData = useLocalObservable(() => new ResellerConsumptionState());
    // 顶部栏数据
    const topBarState = MobxObjectsManager.get(TopBarState);

    // 详情弹窗
    const [isOpenDetail, setIsOpenDetail] = useState<boolean>(false);
    const [detail, setDetail] = useState<ResellerConsumption>({});

    // 解构数据
    const {
        resellerConsumptionList,
        conditions,
        isTableLoading,
        dataTotal,
        viewDateTimeData
    } = resellerConsumptionData;

    useEffect(() => {
        resellerConsumptionData.init();
        topBarState.updateResellerConsumptionTitle('');
        return () => {
            MobxObjectsManager.remove(ResellerConsumptionState);
        }
    }, []);

    // 搜索ref
    const SearchKeyInputRef = React.useRef<{ searchKey: string, setSelectKey: (val: string) => void; }>();

    // 下拉框 左边搜索条件 静态数据
    let filterList: Array<SearchKeyInputItems> = [
        {
            value: 'restaurant_name',
            label: i18n.t('group_management_page_text_restaurant'),
            type: 'TEXT'
        }
    ];
    filterList = getResellerFilter(filterList, true);


    const tableHeadCell: HeadCell[] = [
        { id: 'reseller_name', label: i18n.t('reseller_page_text_reseller'), sortStatus: resellerConsumptionData.getSortStatus('reseller_name') },
        { id: 'group_name', label: i18n.t('group_management_page_text_group'), sortStatus: resellerConsumptionData.getSortStatus('group_name') },
        { id: 'restaurant_name', label: i18n.t('group_management_page_text_restaurant'), sortStatus: resellerConsumptionData.getSortStatus('restaurant_name') },
        {
            id: 'group_detail', label: i18n.t('warrant_page_text_authorization_points'),
            renderFunction: (row: ResellerConsumption) => getAuthPointI18n(row.name || [])
        },
        {
            id: 'price', label: i18n.t('general_text_price'),
            renderFunction: (row: ResellerConsumption) => {
                const { price } = getBalance(row)
                return ` ${row.money_change_type ? '+' : '-'} ${formatEUR(price)}`
            }
        },
        {
            id: 'balance', label: i18n.t('reseller_page_reseller_balance'),
            renderFunction: (row: ResellerConsumption) => {
                const { balance } = getBalance(row)
                return `${formatEUR(Number(row.origin_price || 0))} ➔ ${formatEUR(balance)}`
            }
        },
        {
            id: 'create_date', label: i18n.t('operation_record_operation_time'),
            sortStatus: resellerConsumptionData.getSortStatus('create_date'),
            renderFunction: (row: ResellerConsumption) => {
                return <>
                    {
                        `${moment(row.create_date).format('YYYY/MM/DD HH:mm:ss')}`
                    }
                </>
            }
        },
        {
            id: 'detail', label: '',
            renderFunction: (row: ResellerConsumption) => {
                return !row.money_change_type && row.warrants?.length ? <ButtonComponent action='VIEW' handleClick={() => handleOpenDetail(row)} /> : null
            }
        },
    ];


    /**
     * 打开详情弹窗
     * @param row
     */
    const handleOpenDetail = async (row: ResellerConsumption) => {
        setIsOpenDetail(true);
        setDetail(row)
    }

    // 计算余额
    const getBalance = (row: ResellerConsumption) => {
        // 余额
        let balance = Number(row.origin_price || 0);
        // 扣费金额
        let price = 0;
        if (row.operate_type === 'ADMIN') {
            // 管理员操作
            if (row.money_change_type) {
                balance += Number(row.price || 0)
            } else {
                balance -= Number(row.price || 0)
            }
            price = Number(row.price || 0)
        } else {
            // 购买授权
            balance -= Number(row.discount_price || 0)
            price = Number(row.discount_price || 0)
        }
        return { balance, price };
    }

    // *********************
    // Extra Function
    // *********************


    // 渲染集团列表组件
    const RenderComponent = (): JSX.Element => {

        return (
            <Box>
                <Box className='table-top-tool'>
                    <Box className={'left-tool'}>
                        <SearchKeyInput
                            ref={SearchKeyInputRef}
                            style={{ marginLeft: '8px' }}
                            items={filterList}
                            conditions={toJS(conditions)}
                            onSearchKeyChange={(key) => resellerConsumptionData.setSelectValue(key)}
                            onSearch={async (conditions: ConditionType[]) => {
                                await resellerConsumptionData.setConditions(conditions);
                            }}
                        />
                    </Box >
                    <Box className='right-tool'>
                        <PeriodDateFilter
                            startDate={resellerConsumptionData.startDate}
                            endDate={resellerConsumptionData.endDate}
                            pickerValue={[viewDateTimeData.startDate, viewDateTimeData.endDate]}
                            onDateTimeChange={(dates) => resellerConsumptionData.handleChangePicker(dates)}
                        />
                    </Box>
                </Box >
                <Box>
                    <ConditionsBox conditions={toJS(conditions)} onDelete={(conditions) => {
                        resellerConsumptionData.setConditions(conditions);
                        SearchKeyInputRef?.current?.setSelectKey?.('');
                    }} />
                </Box>
                <TableComponent
                    {...{
                        showChecked: 0,
                        // 表格数据
                        list: resellerConsumptionList ?? [],
                        // 开启表格自定义页脚
                        isUseCustomPagination: true,
                        // 表格头
                        headCellList: tableHeadCell,
                        // 表格加载
                        isTableLoading,
                        // 表格排序
                        handleSort: (headCell) => {
                            resellerConsumptionData.handleSort(headCell);
                        },
                        // 表格行可展开
                        hasExpand: false,
                        // 表格页脚
                        isPagination: true,
                        // 页脚表格数据总数
                        allCount: dataTotal,
                        // 开启折叠组件 防止选择展开按钮会框选到表格行复选框
                        isUseFold: true,
                        // 表格可编辑class类
                        extraClass: 'extraTable'
                    }}
                />

            </Box >
        )
    }

    /**
     * 关闭详情弹窗
     */
    const handleClose = () => {
        setIsOpenDetail(false);
    }

    // *********************
    // Render View
    // *********************

    return (
        (
            <>
                <CardView
                    {...{
                        className: `reseller-consumption-page`,
                        contentRender: RenderComponent(),
                    }}
                />
                <SensitiveOperationsDetail
                    isOpen={isOpenDetail}
                    onClose={handleClose}
                    detail={{
                        operation_type: OperationType.OP_ADD_WARRANT,
                        warrants: detail?.warrants,
                    }}
                />
            </>

        )
    )
})

export default ResellerConsumptionPage;


// *********************
// TYPE
// *********************

